import React, {
    ChangeEvent,
    startTransition,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { matchPath, useLocation } from 'react-router'
import { enteredSearchTerm } from '@components/Analytics/performanceAnalytics'
import { Box, Divider, Paragraph, Stack, TextField } from '@ui'
import { AnimatePresence } from 'framer-motion'
import { useDmChannels } from 'hooks/useDmsAndGdmsAsChannels'
import { useSearch } from 'hooks/useSearch'
import { useShortcut } from 'hooks/useShortcut'
import { useSpaceChannels } from 'hooks/useSpaceChannels'
import { debounce } from 'lodash-es'
import { PATHS } from 'routes'
import { getSectionTitle } from 'routes/TouchSearchTab'
import {
    useDmsAndGdms,
    useSpaceContext,
    useSpaceDataStore,
    useSpaceId,
    useSpaceMembers,
    useThrottledTimelineStore,
} from 'use-towns-client'

import { ResultItem } from './SearchResultItem'
import { CombinedResult } from './types'

export const SearchBar = () => {
    const { spaceId } = useSpaceContext()

    const spaceName = useSpaceDataStore((state) =>
        spaceId ? state.spaceDataMap?.[spaceId]?.name : undefined,
    )

    const location = useLocation()
    const isMessages = matchPath(`/${PATHS.MESSAGES}/*`, location.pathname)
    const searchLabel = isMessages ? `Search messages` : `Search ${spaceName ?? 'Town'}`

    const [isSearchActive, setIsSearchActive] = useState(false)
    const [value, setValue] = useState('')

    const debouncedEnteredSearchTermEvent = useMemo(
        () =>
            debounce((args: { termLength: number }) => {
                enteredSearchTerm(args)
            }, 500),
        [],
    )

    const onChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target) {
            startTransition(() => {
                setValue(e.target.value)
                if (e.target.value.length > 0) {
                    debouncedEnteredSearchTermEvent({ termLength: e.target.value.length })
                }
            })
        }
    }
    const onHide = useCallback(() => {
        setIsSearchActive(false)
    }, [])

    const containerRef = useRef<HTMLDivElement>(null)

    useShortcut(
        'DisplaySearchModal',
        () => {
            setIsSearchActive(true)
        },
        {
            enableOnContentEditable: true,
        },
    )

    useEffect(() => {
        if (isSearchActive) {
            const onClick = (e: MouseEvent) => {
                // allows links to be clicked before dropdown
                setTimeout(() => {
                    setIsSearchActive(false)
                })
            }
            window.addEventListener('pointerup', onClick)
            return () => {
                window.removeEventListener('pointerup', onClick)
            }
        } else {
            setValue('')
        }
    }, [isSearchActive])

    const { searchResults } = useSearch(value)
    const hasActiveResults = isSearchActive && searchResults.length > 0

    useShortcut('DismissDialog', onHide, {
        enableOnFormTags: true,
        enabled: isSearchActive,
    })

    return (
        <Box grow horizontal paddingX="lg" position="relative" width="100%" ref={containerRef}>
            <Box horizontal width="100%" height="100%" position="relative" justifyContent="center">
                <AnimatePresence>
                    <Box
                        // border={isSearchActive}
                        elevateReadability
                        boxShadow={isSearchActive ? 'search' : undefined}
                        style={{ transition: 'box-shadow 0.2s ease-in-out' }}
                        position="absolute"
                        zIndex="tooltipsAbove"
                        rounded="sm"
                        overflow="hidden"
                        maxWidth="100%"
                        width="700"
                    >
                        <Box
                            horizontal
                            grow
                            elevateReadability
                            cursor={isSearchActive ? 'default' : 'pointer'}
                            height="x4"
                            gap="sm"
                            alignItems="start"
                            maxWidth="100%"
                            borderTopLeftRadius="sm"
                            borderTopRightRadius="sm"
                            zIndex="tooltipsAbove"
                            data-testid="top-search-bar"
                            onClick={() => setIsSearchActive(true)}
                        >
                            {isSearchActive ? (
                                <Box horizontal centerContent height="x4">
                                    <TextField
                                        autoFocus
                                        tone="none"
                                        height="100%"
                                        width="700"
                                        placeholder={searchLabel}
                                        defaultValue={value}
                                        data-testid="top-search-bar-input"
                                        onChange={onChange}
                                    />
                                </Box>
                            ) : (
                                <Box horizontal paddingX alignItems="center" gap="sm" height="x4">
                                    <Paragraph color="gray2">{searchLabel}</Paragraph>
                                </Box>
                            )}
                        </Box>
                        {hasActiveResults && (
                            <SearchResults searchResults={searchResults} onHide={onHide} />
                        )}
                    </Box>
                </AnimatePresence>
            </Box>
        </Box>
    )
}

const SearchResults = (props: { onHide: () => void; searchResults: CombinedResult[] }) => {
    const { searchResults } = props

    const location = useLocation()
    const initialLocation = useRef(`${location.pathname}#${location.hash}`)

    useEffect(() => {
        if (`${location.pathname}#${location.hash}` !== initialLocation.current) {
            props.onHide()
        }
    }, [location.hash, location.pathname, props])

    const spaceId = useSpaceId()
    const channels = useSpaceChannels()
    const dmChannels = useDmChannels()
    const { dmsAndGdms } = useDmsAndGdms()

    const members = useSpaceMembers()
    const threadsStats = useThrottledTimelineStore(useCallback((state) => state.threadsStats, []))

    const miscProps = useMemo(
        () => ({
            channels: [...channels, ...dmChannels],
            dmChannelIds: dmsAndGdms,
            members,
            threadsStats,
            spaceId,
        }),
        [channels, dmsAndGdms, dmChannels, members, spaceId, threadsStats],
    )

    const [activeIndex, setActiveIndex] = useState(0)
    const listRef = useRef<HTMLDivElement>(null)
    const handleNavigateDown = useCallback(() => {
        setActiveIndex((a) => Math.min(searchResults.length - 1, a + 1))
    }, [searchResults.length])

    const handleNavigateUp = useCallback(() => {
        setActiveIndex((a) => Math.max(0, a - 1))
    }, [])

    const handleSelectItem = useCallback(() => {
        const el = listRef.current?.querySelector(`#search-item-${activeIndex}`)
        const link = (el?.querySelector('a') ?? el) as HTMLElement
        if ('click' in link) {
            link.click()
            props.onHide()
        }
    }, [activeIndex, props])

    useShortcut('NavigateDown', handleNavigateDown, {
        preventDefault: true,
        ignoreEventWhen: () => false,
    })
    useShortcut('NavigateUp', handleNavigateUp, {
        preventDefault: true,
        ignoreEventWhen: () => false,
    })
    useShortcut('SelectItem', handleSelectItem, {
        preventDefault: true,
        ignoreEventWhen: () => false,
    })

    useEffect(() => {
        listRef.current?.children[activeIndex]?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        })
    }, [activeIndex])

    return (
        searchResults.length > 0 && (
            <Stack width="800" maxWidth="100%" maxHeight="100%" insetTop="xs">
                <Stack overflow="scroll" maxHeight="500">
                    <Box ref={listRef}>
                        {searchResults.map((s, index, items) => {
                            const result = (
                                <ResultItem
                                    result={s}
                                    key={s.item.key}
                                    misc={miscProps}
                                    selected={activeIndex === index}
                                    id={`search-item-${index}`}
                                />
                            )
                            const prevItemType = items[index - 1]?.item?.type
                            if (s.item.type !== prevItemType) {
                                return (
                                    <>
                                        <Divider space="sm" />
                                        <Box
                                            paddingX="md"
                                            paddingY="sm"
                                            key={s.item.type}
                                            color="gray2"
                                        >
                                            <Paragraph>{getSectionTitle(s.item.type)}</Paragraph>
                                        </Box>
                                        {result}
                                    </>
                                )
                            } else {
                                return result
                            }
                        })}
                    </Box>
                </Stack>
            </Stack>
        )
    )
}
